import { useState, useEffect } from 'preact/hooks';
import { fetchGrants, addGrant, revokeGrant, type AdminGrantRow } from '../api/admin';

export function AdminGrantsTab() {
  const [grants, setGrants] = useState<AdminGrantRow[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetchGrants()
      .then(setGrants)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setSaving(true);
    setError('');
    try {
      await addGrant(newEmail.trim());
      setNewEmail('');
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (email: string) => {
    if (!confirm(`Revoke admin grant for ${email}?`)) return;
    setError('');
    try {
      await revokeGrant(email);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const active = grants.filter((g) => !g.revokedAt);
  const revoked = grants.filter((g) => g.revokedAt);

  return (
    <div class="admin-grants-tab">
      {error && <div class="message error" style={{ margin: '0 0 12px' }}>{error}</div>}

      <div class="admin-grant-add" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="email"
          placeholder="user@example.com"
          value={newEmail}
          onInput={(e) => setNewEmail((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border-medium)',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
        />
        <button
          class="btn-resume"
          onClick={handleAdd}
          disabled={saving || !newEmail.trim()}
        >
          {saving ? 'Adding…' : 'Grant Admin'}
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
            Active Grants ({active.length})
          </h3>
          {active.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 16 }}>No active grants</div>
          ) : (
            <div class="admin-grant-list" style={{ marginBottom: 16 }}>
              {active.map((g) => (
                <div
                  key={g.email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'var(--bg-elevated)',
                    marginBottom: 4,
                  }}
                >
                  <div>
                    <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{g.email}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 8 }}>
                      by {g.grantedBy} · {new Date(g.grantedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    class="btn-kill"
                    style={{ fontSize: 11, padding: '4px 10px' }}
                    onClick={() => handleRevoke(g.email)}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          {revoked.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                Revoked ({revoked.length})
              </h3>
              <div class="admin-grant-list">
                {revoked.map((g) => (
                  <div
                    key={g.email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: 6,
                      background: 'var(--bg-elevated)',
                      marginBottom: 4,
                      opacity: 0.5,
                    }}
                  >
                    <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{g.email}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 8 }}>
                      revoked {new Date(g.revokedAt!).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
